// Cari Posisi Barang (Gudang Rido) — tampilan sederhana: cari → 3 indikator tindakan → filter dropdown → daftar.
import { useState } from "react";
import { AlertTriangle, Zap, Bell, SlidersHorizontal, X, Info } from "lucide-react";
import { getChannel } from "./channel";

const DIVISIONS = ["Mockup", "Gudang", "Bordir", "Sablon/DTF", "Press", "Sublim", "Finishing", "Toko"];
const HOUR = 3600 * 1000;

export function SimpleIndicators({ data, isShopee, status, setStatus, unassigned, setUnassigned, cancelOpen, setCancelOpen }) {
  const s = data.summary || {};
  const action = isShopee ? data.cancel_summary?.needs_action : data.unassigned_count;
  const actionOn = isShopee ? cancelOpen : unassigned;
  const tiles = [
    { k: "overdue", label: "Terlambat", v: s.terlambat, Icon: AlertTriangle, tone: "late", on: status === "overdue", click: () => setStatus(status === "overdue" ? "all" : "overdue") },
    { k: "today", label: isShopee ? "Wajib Kirim Hari Ini" : "Deadline Hari Ini", v: s.hari_ini, Icon: Zap, tone: "today", on: status === "today", click: () => setStatus(status === "today" ? "all" : "today") },
    { k: "action", label: "Perlu Tindakan", sub: isShopee ? "pembatalan Shopee" : "belum ada operator", v: action, Icon: Bell, tone: "act", on: actionOn,
      click: () => (isShopee ? setCancelOpen(!cancelOpen) : setUnassigned(!unassigned)) },
  ];
  return <div className="pms-ind" data-testid="monitor-summary">
    {tiles.map(t => <button key={t.k} type="button" className={`pms-tile pms-${t.tone} ${t.on ? "on" : ""}`} onClick={t.click} aria-pressed={t.on}
      data-testid={`monitor-count-${t.k}`}>
      <strong data-testid={`monitor-count-${t.k}-value`}>{t.v ?? "—"}</strong>
      <span><t.Icon size={14} aria-hidden="true"/> {t.label}{t.sub && <small>{t.sub}</small>}</span>
    </button>)}
  </div>;
}

export function SyncWarning({ data, isShopee, onInfo }) {
  // Peringatan sinkron API hanya untuk kanal Shopee — Tokopedia masih input manual (belum terhubung API).
  if (!isShopee || getChannel() === "tokopedia") return null;
  const last = data.overall?.last_sync ? new Date(data.overall.last_sync).getTime() : null;
  const stale = data.overall && (!last || Date.now() - last > 2 * HOUR);
  const mismatch = data.coverage?.no_shopee_match || 0;
  const noDl = data.overall?.no_deadline || 0;
  if (!stale && !mismatch && !noDl) return null;
  const parts = [stale && (last ? "Data Shopee belum diperbarui lebih dari 2 jam" : "Data Shopee belum pernah tersinkron"),
    mismatch && `${mismatch} pesanan nomornya tidak cocok dengan Shopee`, noDl && `${noDl} pesanan belum punya batas kirim`].filter(Boolean);
  return <div className="pms-warn" role="status" data-testid="monitor-data-warning">
    <AlertTriangle size={14} aria-hidden="true"/> {parts.join(" · ")}. Angka bisa belum lengkap.
    <button type="button" className="pms-link" onClick={onInfo} data-testid="monitor-data-warning-link">Lihat rincian</button>
  </div>;
}

export function SimpleFilters({ isShopee, data, division, setDivision, unassigned, setUnassigned, blocked, setBlocked, status, setStatus, pic, setPic, picPicker }) {
  const [more, setMore] = useState(false);
  const counts = data.division_counts || {};
  const active = [
    division && { k: "div", t: `Divisi: ${division}`, off: () => setDivision("") },
    pic && { k: "pic", t: `Operator: ${pic}`, off: () => setPic("") },
    unassigned && { k: "un", t: "Belum ada operator", off: () => setUnassigned(false) },
    blocked && { k: "bl", t: "Menunggu ACC Barang", off: () => setBlocked(false) },
    status !== "all" && { k: "st", t: ({ overdue: "Terlambat", today: isShopee ? "Kirim Hari Ini" : "Deadline Hari Ini", near: isShopee ? "Kirim Besok" : "Mendekat" })[status] || status, off: () => setStatus("all") },
  ].filter(Boolean);
  const reset = () => { setDivision(""); setPic(""); setUnassigned(false); setBlocked(false); setStatus("all"); };
  return <div className="pms-filters">
    <div className="pms-filter-row">
      <h4 className="pms-h">Daftar Pesanan <span className="pms-total" data-testid="monitor-chip-all">{data.summary?.semua ?? 0} aktif</span></h4>
      <select className="pms-select" value={division} onChange={e => setDivision(e.target.value)} aria-label="Divisi" data-testid="monitor-division-filter">
        <option value="">Semua Divisi</option>
        {DIVISIONS.map(d => <option key={d} value={d}>{d} ({counts[d] || 0})</option>)}
      </select>
      {picPicker}
      <button type="button" className={`pms-more ${more ? "on" : ""}`} onClick={() => setMore(v => !v)} aria-expanded={more} data-testid="monitor-more-filters">
        <SlidersHorizontal size={14}/> Filter Lainnya</button>
    </div>
    {more && <div className="pms-more-body" data-testid="monitor-more-filters-body">
      <button type="button" className={`pm-div ${unassigned ? "active" : ""}`} onClick={() => setUnassigned(!unassigned)} data-testid="monitor-div-unassigned">
        Belum Ditugaskan <span>{data.unassigned_count || 0}</span></button>
      {isShopee && <button type="button" className={`pm-div ${blocked ? "active" : ""}`} onClick={() => setBlocked(!blocked)} data-testid="monitor-div-blocked">
        Menunggu ACC Barang Gudang <span data-testid="monitor-div-blocked-count">{data.blocked_count || 0}</span></button>}
      <button type="button" className={`pm-div ${status === "near" ? "active" : ""}`} onClick={() => setStatus(status === "near" ? "all" : "near")} data-testid="monitor-count-near">
        {isShopee ? "Kirim Besok" : "Deadline Mendekat"} <span data-testid="monitor-count-near-value">{data.summary?.mendekat ?? 0}</span></button>
    </div>}
    {active.length > 0 && <div className="pms-active" data-testid="monitor-active-filters">
      {active.map(a => <button key={a.k} type="button" className="pms-chip" onClick={a.off} data-testid={`monitor-active-${a.k}`}>{a.t} <X size={12}/></button>)}
      <button type="button" className="pms-link" onClick={reset} data-testid="monitor-filter-reset">Reset</button>
    </div>}
  </div>;
}

export function DataInfoPanel({ open, setOpen, children }) {
  return <section className="pms-info" data-testid="monitor-data-info">
    <button type="button" className="pms-info-head" onClick={() => setOpen(!open)} aria-expanded={open} data-testid="monitor-data-info-toggle">
      <Info size={14}/> Informasi Data <span aria-hidden="true">{open ? "▲" : "▼"}</span></button>
    {open && <div className="pms-info-body">{children}</div>}
  </section>;
}
